import React from "react";
import { Platform, ScrollView } from "react-native";
import { Text, View } from "native-base";
import { History, ShoppingCart } from "lucide-react-native";
import { webFont } from "../../Universal/API/API";
import PressableWrapp from "../../Universal/Buttons/PressableReact/PressableWrapp";
import type { OrderHistorySummary } from "../cartTypes";
import { formatCurrency, formatOrderHistoryDate } from "../helpers";

const webCursorPointer = Platform.OS === "web" ? ({ cursor: "pointer" } as any) : null;

type CartHistorySectionProps = {
  scaleAll: number;
  isSmallVersion: boolean;
  orders: OrderHistorySummary[];
  showDiscountPrices: boolean;
  selectedOrderId?: string | null;
  isHistoryMode: boolean;
  onOpenCurrentCart: () => void;
  onOpenHistoryOrder: (orderId: string) => void;
};

const getOrderCardTotal = ({
  order,
  showDiscountPrices,
}: {
  order: OrderHistorySummary;
  showDiscountPrices: boolean;
}) => {
  if (showDiscountPrices) {
    return order.saved_opt_total > 0 ? order.saved_opt_total : order.saved_rrc_total;
  }

  return order.saved_rrc_total > 0 ? order.saved_rrc_total : order.total_price;
};

const CartHistorySection: React.FC<CartHistorySectionProps> = ({
  scaleAll,
  isSmallVersion,
  orders,
  showDiscountPrices,
  selectedOrderId,
  isHistoryMode,
  onOpenCurrentCart,
  onOpenHistoryOrder,
}) => {
  if (!orders.length) {
    return null;
  }

  return (
    <View style={{ marginBottom: 18 * scaleAll }}>
      <Text
        selectable={false}
        style={{
          fontFamily: webFont("Inter"),
          fontSize: 11 * scaleAll,
          color: "#7A746C",
          fontWeight: "700",
          textTransform: "uppercase",
          letterSpacing: 0.7,
          marginBottom: 10 * scaleAll,
        }}
      >
        История заказов
      </Text>

      <ScrollView
        horizontal={true}
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ paddingRight: 8 * scaleAll }}
      >
        <PressableWrapp
          onPress={onOpenCurrentCart}
          style={[
            {
              width: isSmallVersion ? 220 * scaleAll : 250 * scaleAll,
              minHeight: 112 * scaleAll,
              borderRadius: 24 * scaleAll,
              backgroundColor: isHistoryMode ? "#FFFFFF" : "#111111",
              borderWidth: 1,
              borderColor: isHistoryMode ? "#ECE8E1" : "#111111",
              padding: 16 * scaleAll,
              marginRight: 10 * scaleAll,
              justifyContent: "space-between",
            },
            webCursorPointer,
          ]}
        >
          <View style={{ flexDirection: "row", alignItems: "center" }}>
            <View
              style={{
                width: 36 * scaleAll,
                height: 36 * scaleAll,
                borderRadius: 14 * scaleAll,
                backgroundColor: isHistoryMode ? "#F7F5F0" : "rgba(255,255,255,0.14)",
                alignItems: "center",
                justifyContent: "center",
                marginRight: 10 * scaleAll,
              }}
            >
              <ShoppingCart
                size={18 * scaleAll}
                color={isHistoryMode ? "#111111" : "#FFFFFF"}
              />
            </View>

            <Text
              selectable={false}
              style={{
                fontFamily: webFont("Inter"),
                fontSize: 13 * scaleAll,
                color: isHistoryMode ? "#111111" : "#FFFFFF",
                fontWeight: "700",
              }}
            >
              Текущий заказ
            </Text>
          </View>

          <Text
            selectable={false}
            style={{
              fontFamily: webFont("Inter"),
              fontSize: 12 * scaleAll,
              color: isHistoryMode ? "#7A746C" : "rgba(255,255,255,0.75)",
              fontWeight: "600",
            }}
          >
            Вернуться к редактированию корзины
          </Text>
        </PressableWrapp>

        {orders.map((order) => {
          const isSelected = isHistoryMode && selectedOrderId === order.order_id;
          const total = getOrderCardTotal({ order, showDiscountPrices });
          const dateText = formatOrderHistoryDate(order.updated_at || order.created_at);

          return (
            <PressableWrapp
              key={order.order_id}
              onPress={() => onOpenHistoryOrder(order.order_id)}
              style={[
                {
                  width: isSmallVersion ? 240 * scaleAll : 280 * scaleAll,
                  minHeight: 112 * scaleAll,
                  borderRadius: 24 * scaleAll,
                  backgroundColor: isSelected ? "#111111" : "#FFFFFF",
                  borderWidth: 1,
                  borderColor: isSelected ? "#111111" : "#ECE8E1",
                  padding: 16 * scaleAll,
                  marginRight: 10 * scaleAll,
                  justifyContent: "space-between",
                },
                webCursorPointer,
              ]}
            >
              <View style={{ flexDirection: "row", alignItems: "center" }}>
                <View
                  style={{
                    width: 36 * scaleAll,
                    height: 36 * scaleAll,
                    borderRadius: 14 * scaleAll,
                    backgroundColor: isSelected ? "rgba(255,255,255,0.14)" : "#F7F5F0",
                    alignItems: "center",
                    justifyContent: "center",
                    marginRight: 10 * scaleAll,
                  }}
                >
                  <History
                    size={18 * scaleAll}
                    color={isSelected ? "#FFFFFF" : "#111111"}
                  />
                </View>

                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text
                    selectable={false}
                    numberOfLines={1}
                    style={{
                      fontFamily: webFont("Inter"),
                      fontSize: 15 * scaleAll,
                      color: isSelected ? "#FFFFFF" : "#111111",
                      fontWeight: "700",
                    }}
                  >
                    {order.order_id}
                  </Text>
                  <Text
                    selectable={false}
                    style={{
                      fontFamily: webFont("Inter"),
                      fontSize: 11 * scaleAll,
                      color: isSelected ? "rgba(255,255,255,0.72)" : "#7A746C",
                      fontWeight: "600",
                      marginTop: 2 * scaleAll,
                    }}
                  >
                    {dateText || "Без даты"}
                  </Text>
                </View>
              </View>

              <View>
                <Text
                  selectable={false}
                  style={{
                    fontFamily: webFont("Inter"),
                    fontSize: 11 * scaleAll,
                    color: isSelected ? "rgba(255,255,255,0.72)" : "#7A746C",
                    fontWeight: "700",
                    textTransform: "uppercase",
                    letterSpacing: 0.65,
                    marginBottom: 4 * scaleAll,
                  }}
                >
                  {order.total_quantity} шт. • {order.items_count} поз.
                </Text>
                <Text
                  selectable={false}
                  style={{
                    fontFamily: webFont("Inter"),
                    fontSize: 20 * scaleAll,
                    color: isSelected ? "#FFFFFF" : "#111111",
                    fontWeight: "700",
                  }}
                >
                  {formatCurrency(total)} ₽
                </Text>
              </View>
            </PressableWrapp>
          );
        })}
      </ScrollView>
    </View>
  );
};

export default React.memo(CartHistorySection);
